import {
  Collapse,
  IconButton,
  List,
  ListItemText,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft"
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight"
import { useDebounceValue } from "usehooks-ts"
import React, { FunctionComponent, useEffect, useState } from "react"
import { XYPosition, useReactFlow, useStoreApi } from "reactflow"
import { GraphStoreType } from "@features/graph/Graph"
import { GraphNodeType } from "@custom-types/graphs"
import { GraphDataViewModel } from "@features/graph/graphViewModel"
import { expandGroupNode } from "@features/graph/handlers/graphGroupCollapseExpand"
import { GraphConverter } from "@store/services/converter/graphConverter"
import { GraphNodeData, GroupNodeData } from "@store/services/converter/types"
import CloseIcon from "@mui/icons-material/Close"
import { graphNodeClickHandler } from "@features/graph/handlers/graphNodeClick"
import ListItem from "@mui/material/ListItem"
import Grid from "@mui/material/Grid"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import { ZoomIn, ZoomOut } from "@mui/icons-material"
import Button from "@mui/material/Button"
import Fuse, { FuseResult } from "fuse.js"
import { graphUrlAddNode } from "@features/graph/utils/graphUrl"
import { useSetNodeSelection } from "@features/graph/state/graphAtoms"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import { useSearchParams } from "react-router"
import { get } from "lodash"

type SearchNodeType = {
  groupIds: string[] | null
  nodeId: string
  position: XYPosition | undefined
  format: string | undefined
}

type NodeSearchPopperProps = {
  onCloseClick: () => void
  showAddToSelection?: boolean
}

type FuseBody = {
  label: string | undefined
  nodeId: string
  node: GraphNodeData | undefined
  format: string | undefined
  groups: (string | undefined)[] | undefined
}

function searchFuse(searchTerm: string, keys: string[], items: FuseBody[]) {
  const options = {
    includeScore: true,
    useExtendedSearch: true,
    keys: keys,
  }
  const fuse = new Fuse(items, options)
  const searchTerms = searchTerm.match(/(".*?"|[^"\s]+)+(?=\s*|\s*$)/g) //.map(x=>x.replaceAll('"', ""))
  // we want to allow space to act as AND by default, not fuzzy matching
  // so we prepend ' to each word not already having a modifier
  // BUT- we must protect something in quotes already e.g. ='find this thing'

  //https://www.fusejs.io/examples.html#extended-searchToken
  // White space acts as an AND operator, while a single pipe (|) character acts as an OR operator.
  // Match      type	                           Description
  // jscript	  fuzzy-match	Items                that fuzzy match jscript  [X]
  // =scheme	  exact-match	Items                that are scheme
  // 'python	  include-match	Items              that include python
  // !ruby	    inverse-exact-match	Items        that do not include ruby
  // ^java	    prefix-exact-match Items         that start with java
  // !^earlang	inverse-prefix-exact-match Items that do not start with earlang
  // .js$	      suffix-exact-match Items         that end with .js
  // !.go$	    inverse-suffix-exact-match Items that do not end with .go

  // when the user provides an empty search term, treat it specially:
  // return items where at least one of the provided keys has any value
  const trimmed = searchTerm.trim()
  if (trimmed === "") {
    const matches = items.filter((fuseItem) =>
      keys.some((key) => {
        const valueAtPath = get(fuseItem, key)

        return valueAtPath !== undefined && valueAtPath !== null && valueAtPath !== ""
      }),
    )

    const results = matches.map((matchedItem) => ({ item: matchedItem, score: 0, refIndex: 0 }))
    return results
  }

  // check no starting or ending modifier
  const search = searchTerms
    ?.map((x: string) => {
      return x[0]?.match(/^[0-9A-Za-z]$/) && x[x.length - 1]?.match(/^[0-9A-Za-z]$/) ? "'" + x : x
    })
    .join(" ")

  if (search) {
    const result = fuse.search(search)
    return result
  }
  return []
}

export const NodeSearchPopper: FunctionComponent<NodeSearchPopperProps> = ({
  onCloseClick,
  showAddToSelection,
}) => {
  const [searchParams, setSearchParams] = useSearchParams({})
  const { addNode } = useSetNodeSelection()
  const [debouncedValue, setValue] = useDebounceValue("", 300)
  const [state, setState] = useState<{ nodes: SearchNodeType[]; currentIndex: number }>({
    nodes: [],
    currentIndex: 0,
  })
  const [zoomLevel, setZoomLevel] = useState(2.5)
  const [searchType, setSearchType] = useState(["label"])
  const [searchTypeLabel, setSearchTypeLabel] = useState("label")
  const graphStore: GraphStoreType = useStoreApi()

  const { fitView, getNode } = useReactFlow()

  // link is exception and we want to show all nodes with link even if search is empty
  const isSearching = debouncedValue !== "" || searchTypeLabel === "link"

  useEffect(() => {
    if (!isSearching) {
      setState({
        nodes: [],
        currentIndex: 0,
      })
      return
    }
    const graphState = graphStore.getState()
    const nodes = graphState.getNodes()
    const graphData = GraphDataViewModel.data
    const result: Map<string, SearchNodeType> = new Map()

    const searchInGroup = function (nodeGroup: GroupNodeData, groupIds: string[]) {
      const suggestions = searchFuse(
        debouncedValue,
        searchType,
        nodeGroup.group.nodes.map((x) => {
          const node = graphData?.nodeMap[x]
          return {
            nodeId: x,
            node: node,
            // fields needed to search on
            format: node?.display["Unit Format"],
            label: graphData?.nodeMap[x]?.labelMap[x],
            groups: node?.display.groups?.map((x: string) => graphData?.groupMap[x]?.group.label),
          }
        }),
      )
      suggestions.forEach((suggestion) => {
        const prevResult = result.get(suggestion.item.nodeId)
        if (prevResult) {
          // take only longest path in subgroups
          if (prevResult.groupIds == null || prevResult.groupIds?.length < groupIds.length) {
            result.set(suggestion.item.nodeId, {
              groupIds: [...groupIds],
              nodeId: suggestion.item.nodeId,
              position: suggestion.item.node?.position,
              format: suggestion.item.node?.display["Unit Format"],
            })
          }
        } else {
          result.set(suggestion.item.nodeId, {
            groupIds: [...groupIds],
            nodeId: suggestion.item.nodeId,
            position: suggestion.item.node?.position,
            format: suggestion.item.node?.display["Unit Format"],
          })
        }
      })

      const subGroups = graphData?.childrenGroupMap[nodeGroup.id]
      if (subGroups) {
        subGroups.forEach((subGroupId) => {
          const subGroup = graphData?.groupMap[subGroupId]
          if (subGroup) {
            searchInGroup(subGroup, [...groupIds, subGroupId])
          }
        })
      }
    }

    const non_group_nodes: FuseBody[] = nodes
      .map((x) => {
        if (x.type !== GraphNodeType.GROUP) {
          const node: GraphNodeData | undefined = graphData?.nodeMap[x.id]
          return {
            nodeId: x.id.toString(),
            node: node,
            // fields needed to search on
            format: node?.display["Unit Format"],
            label: node?.display.label,
            groups: node?.display.groups?.map((x: string) => graphData?.groupMap[x]?.group.label),
          }
        }
        return undefined
      })
      .filter((i) => i !== undefined) as FuseBody[]

    const nodeSuggestions = searchFuse(debouncedValue, searchType, non_group_nodes)
    nodeSuggestions.map((x: FuseResult<FuseBody>) => {
      const prevResult = result.get(x.item.nodeId)
      if (prevResult) {
        // take only longest path in subgroups
        if (prevResult.groupIds == null || prevResult.groupIds?.length === 0) {
          result.set(x.item.nodeId, {
            groupIds: null,
            nodeId: x.item.nodeId,
            position: x.item.node?.position,
            format: x.item.node?.display["Unit Format"],
          })
        }
      } else {
        result.set(x.item.nodeId, {
          groupIds: null,
          nodeId: x.item.nodeId,
          position: x.item.node?.position,
          format: x.item.node?.display["Unit Format"],
        })
      }
    })

    nodes.forEach((node) => {
      if (node.type === GraphNodeType.GROUP) {
        const groupMapData = graphData?.groupMap[node.id]
        if (groupMapData) {
          searchInGroup(groupMapData, [node.id])
        }
      }
    })

    setState({
      nodes: [...result.values()],
      currentIndex: 0,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [debouncedValue, searchType, isSearching])

  useEffect(() => {
    if (!state.currentIndex) {
      return
    }
    const searchNode = state.nodes[state.currentIndex - 1]
    const graphState = graphStore.getState()
    const graphData = GraphDataViewModel.data

    if (searchNode?.groupIds?.length) {
      const groupIds = [...searchNode.groupIds]
      groupIds.forEach((groupId) => {
        const node = graphData?.groupMap[groupId]
        if (node && GraphDataViewModel.data) {
          expandGroupNode(node, GraphDataViewModel.data, graphStore)
        }
      })
      graphState.resetSelectedElements()
      graphState.unselectNodesAndEdges()
      graphNodeClickHandler(searchNode.nodeId, graphStore)

      // wait for reactflow state update
      setTimeout(() => {
        const n = getNode(searchNode.nodeId)
        if (n) {
          fitView({ nodes: [n], duration: 0, maxZoom: zoomLevel })
        }
      }, 0)
    } else {
      graphState.resetSelectedElements()
      graphState.unselectNodesAndEdges()
      graphNodeClickHandler(searchNode?.nodeId ?? "", graphStore)

      setTimeout(() => {
        const n = getNode(searchNode?.nodeId ?? "")
        if (n) {
          fitView({ nodes: [n], duration: 0, maxZoom: zoomLevel })
        }
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [state.currentIndex])

  const onPrevClick = () => {
    let newIndex = state.currentIndex - 1
    if (newIndex <= 0) {
      newIndex = state.nodes.length
    }
    setState({
      ...state,
      currentIndex: newIndex,
    })
  }

  const onNextClick = () => {
    let newIndex = state.currentIndex + 1
    if (newIndex > state.nodes.length) {
      newIndex = 1
    }
    setState({
      ...state,
      currentIndex: newIndex,
    })
  }

  function incrementZoom() {
    setZoomLevel(zoomLevel + 0.25)
  }
  function decrementZoom() {
    setZoomLevel(zoomLevel - 0.25)
  }

  function handleTypeChange(val: string[], label: string) {
    setSearchType(val)
    setSearchTypeLabel(label)
  }

  const handleAddSelection = (nodeId: string) => {
    const data = GraphDataViewModel.data?.nodeMap[nodeId]
    if (!data) {
      return
    }
    const node = GraphConverter.createNodeFromNodeData(data)
    addNode(node)
    graphUrlAddNode(data.display.label)
  }

  const handleSelectNodeItem = (itemIndex: number) => {
    setState({
      ...state,
      currentIndex: itemIndex + 1,
    })

    // toggle off categories view in case active
    setSearchParams((params) => {
      params.set("isCategoriesViewEnabled", false.toString())
      return params
    })
  }

  return (
    <Paper
      sx={{
        p: 1,
        mt: "1px",
        ml: "-2px",
        boxShadow:
          "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
        borderRadius: "5px !important",
        border: "1px solid #ccc !important",
      }}
    >
      <Grid container sx={{ marginBottom: "1px" }}>
        <ToggleButtonGroup value={searchTypeLabel} size="small" aria-label="Basic button group">
          <ToggleButton
            value={"name"}
            sx={{ fontSize: "10pt" }}
            onClick={(x) => handleTypeChange(["label"], "name")}
          >
            Name
          </ToggleButton>
          <ToggleButton
            value={"unit"}
            sx={{ fontSize: "10pt" }}
            onClick={(x) => handleTypeChange(["format"], "unit")}
          >
            Unit
          </ToggleButton>
          <ToggleButton
            value={"mnemonic"}
            sx={{ fontSize: "10pt" }}
            onClick={(x) =>
              handleTypeChange(
                [
                  "node.display.Calibration Mnem",
                  "node.display.Scenario Mnem",
                  "node.display.Forecast Mnem",
                  "node.display.Historic Mnem",
                ],
                "mnemonic",
              )
            }
          >
            Mnemonic
          </ToggleButton>

          <ToggleButton
            value={"description"}
            sx={{ fontSize: "10pt" }}
            onClick={(x) => handleTypeChange(["node.display.Description"], "description")}
          >
            Description
          </ToggleButton>

          <ToggleButton
            value={"group"}
            sx={{ fontSize: "10pt" }}
            onClick={(x) => handleTypeChange(["groups"], "group")}
          >
            Group
          </ToggleButton>

          <ToggleButton
            value={"type"}
            sx={{ fontSize: "10pt" }}
            onClick={(x) => handleTypeChange(["node.type"], "type")}
          >
            Type
          </ToggleButton>
          <ToggleButton
            value={"link"}
            sx={{ fontSize: "10pt" }}
            onClick={() => handleTypeChange(["node.display.Link"], "link")}
          >
            Link
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>
      <TextField
        label={`Search ${searchTypeLabel}s`}
        variant="standard"
        sx={{ width: "300px" }}
        onChange={(event) => setValue(event.target.value)}
      />
      <IconButton
        aria-label="Previous"
        sx={{ verticalAlign: "bottom" }}
        disabled={state.nodes.length === 0}
        onClick={onPrevClick}
      >
        <KeyboardDoubleArrowLeftIcon />
      </IconButton>
      <IconButton
        aria-label="Next"
        sx={{ verticalAlign: "bottom" }}
        disabled={state.nodes.length === 0}
        onClick={onNextClick}
      >
        <KeyboardDoubleArrowRightIcon />
      </IconButton>
      <IconButton aria-label="Close" sx={{ verticalAlign: "bottom" }} onClick={onCloseClick}>
        <CloseIcon />
      </IconButton>
      <Card
        sx={{
          width: "100%",
          maxHeight: "300px",
          overflowY: "scroll",
          boxShadow: "none",
          borderRadius: "5px !important",
        }}
      >
        <CardContent
          sx={{
            paddingBottom: "5px !important",
            padding: "5px",
            mb: "0px",
            mt: "0px",
            cursor: "pointer",
            borderBottom: "1px solid #e7e7e7",
          }}
        >
          <Typography
            variant={"caption"}
          >{`${state.currentIndex} of ${state.nodes.length} nodes`}</Typography>
        </CardContent>
        <Collapse in={state.nodes.length >= 1} timeout="auto" unmountOnExit>
          <CardContent
            sx={{
              paddingBottom: "5px !important",
              padding: "3px",
              mb: "0px",
              mt: "0px",
              cursor: "pointer",
            }}
          >
            <Grid>
              <List>
                {state.nodes.map((p, i) => (
                  <ListItem
                    divider
                    key={p.nodeId}
                    onClick={() => handleSelectNodeItem(i)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(225, 225, 225, 0.4)",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <span style={{ fontSize: showAddToSelection ? "0.8rem" : "1rem" }}>
                          {GraphDataViewModel.data?.labelMap[p.nodeId]}
                        </span>
                      }
                      secondary={
                        <span style={{ fontSize: showAddToSelection ? "0.8rem" : "1rem" }}>
                          {formatValuePythonic("", p.format)}
                        </span>
                      }
                    />
                    {showAddToSelection && (
                      <Button
                        sx={{
                          paddingLeft: 5,
                          paddingRight: 5,
                          width: 75,
                          fontSize: "0.8rem",
                          borderRadius: "3px !important",
                          backgroundColor: "rgba(40,40,43, 0.9)",
                        }}
                        size="small"
                        onClick={() => handleAddSelection(p.nodeId)}
                        variant="contained"
                      >
                        ADD TO SELECTION
                      </Button>
                    )}
                  </ListItem>
                ))}
              </List>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>
      <Grid
        container
        sx={{
          p: "3px",
          color: "#777",
          wordBreak: "break-word",
          wordWrap: "break-word",
          maxWidth: "450px",
        }}
      >
        {!isSearching && (
          <Grid item xs={12} sx={{ maxHeight: "150px", overflowY: "scroll" }}>
            <Typography variant="body2">
              <b>SEARCH ON DIFFERENT THINGS</b> you must choose what you need to search on using the
              bar along the top (default is node name), so you can find all 'deterministic' nodes
              using type, or all nodes in the group, or all nodes with a certain unit etc.
            </Typography>
            <Typography variant="body2">
              <b>MULTIPLE TERMS</b> you can enter multiple terms, not necessarily connected together
            </Typography>
            <Typography variant="body2">
              <b>AT-START</b> if you need to match something at the start of the node name you can
              use ^, e.g. ^USA
            </Typography>
            <Typography variant="body2">
              <b>AT-END</b> Similarly, to match something at the end of the node name use $, e.g.
              USA price$
            </Typography>
            <Typography variant="body2">
              <b>EXCLUSION</b> To exclude a match use !, e.g. CAN industry !diff !lag1 e.g. pct
              change !USA
            </Typography>
          </Grid>
        )}
        <Grid item xs={8} sx={{ marginTop: "4px" }}>
          <Typography>Zoom level on navigation: {zoomLevel.toFixed(2)}x</Typography>
        </Grid>
        <Grid item xs={2}>
          <Button onClick={decrementZoom} sx={{ width: "10px" }} endIcon={<ZoomOut />}></Button>
        </Grid>
        <Grid item xs={2}>
          <Button onClick={incrementZoom} sx={{ width: "10px" }} endIcon={<ZoomIn />}></Button>
        </Grid>
      </Grid>
    </Paper>
  )
}
